<template>

  <div id="map"></div>

</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import { useMetadataStore } from "@/stores/metadataStore";
import { Map, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Style, Circle, Fill, Stroke } from 'ol/style';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import type { PropType } from "vue";
import type { GeoJSONGeometryCollection } from "ol/format/GeoJSON";

export default defineComponent({
  props: {
    geoJSONCollection: Object as PropType<GeoJSONGeometryCollection>,
  },
  setup(props) {
    const map = ref<Map>();
    const mStore = useMetadataStore();
    const vectorSource = new VectorSource<Feature<Geometry>>();
    const styleCache = new globalThis.Map<string, Style>();

    const getStyle = (speciesGroup : string) => {
      if (styleCache.has(speciesGroup)) {
        return styleCache.get(speciesGroup);
      }
      let fillColor = 'black';
      if (speciesGroup) {
        switch (speciesGroup) {
          case 'plant':
            fillColor = '#537A29'; // Brighter green
            break;
          case 'protista':
            fillColor = '#219EBC'; // Blue is generally okay
            break;
          case 'vertebrate':
            fillColor = '#FFBF1F'; // Distinct golden yellow
            break;
          case 'invertebrate':
            fillColor = '#C1121F'; // Bright yellow
            break;
          case 'fungi':
            fillColor = '#023047'; // Dark red
            break;
        }
      }


      const style = new Style({
        image: new Circle({
          radius: 5.5,
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: 'white', width: 1 }),
        }),
      });

      styleCache.set(speciesGroup, style);
      return style;
    };

    onMounted(async () => {
      await mStore.fetchMetadata();
      watch(() => props.geoJSONCollection, (newValue) => {
        if (!newValue) return;
        const newFeatures: Feature<Geometry>[] = new GeoJSON().readFeatures(newValue, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:4326',
        }) as Feature<Geometry>[];

        vectorSource.clear();
        vectorSource.addFeatures(newFeatures);

        newFeatures.forEach((feature) => {
          const popId = feature.get('gendib_pop_id');
          const speciesGroup = mStore.speciesGroupMap.get(popId);
          feature.setStyle(getStyle(speciesGroup));
        });
      }, { immediate: true });

      map.value = new Map({
        target: 'map',
        layers: [
          new TileLayer({ source: new OSM() }),
          new VectorLayer({ source: vectorSource })
        ],
        view: new View({
          center: [9, 47],
          projection: 'EPSG:4326',
          zoom: 8.5,
        }),
      });

      map.value.on('singleclick', (event) => {
        map.value?.forEachFeatureAtPixel(event.pixel, (feature) => {
          const id = feature.get('gendib_pop_id');
          mStore.selectRecord(id);
        });
      });
    });

    return { map };
  },
});
</script>
